import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
  CircularProgress
} from "@mui/material";
import { ChangeEvent, useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import SwitchToggle from "../../components/SwitchToggle";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import {
  useGetBrandsQuery,
  useGetModelsQuery,
  useGetCategoriesQuery,
  useGetCategoryDetailsQuery,
  useAddSparePartMutation,
  useUpdateSparePartMutation,
  useGetSparePartDetailsQuery,
  useUploadMediaMutation
} from "../../service/features/api";
import { VEHICLE_TYPE, VEHICLE_CONDITION } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";
import { toast } from "react-toastify";
import { useGetShowroomsQuery } from "../../service/showroom/api";

const AddEditSparePart = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const location = useLocation();
  const defaultType = location.state?.defaultType;

  const [images, setImages] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<any>(null);
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [modelId, setModelId] = useState("");
  const [conditionId, setConditionId] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState<number | "">(defaultType || "");
  const [name, setName] = useState("");
  const [dealer, setDealer] = useState(""); // UI state
  const [oemNumber, setOemNumber] = useState("");
  const [actualPrice, setActualPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [isFeatured, setIsFeatured] = useState(false);

  const [addSparePart, { isLoading: isAdding }] = useAddSparePartMutation();
  const [updateSparePart] = useUpdateSparePartMutation();
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  // API Queries
  const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100, type: selectedVehicleType });
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, type: selectedVehicleType });
  const { data: categoryDetails, isLoading: isSubCatLoading } = useGetCategoryDetailsQuery(
    categoryId,
    { skip: !categoryId, refetchOnMountOrArgChange: true }
  );
  // const { data: conditionsData } = useGetConditionsQuery({ page: 1, limit: 100 });
  const { data: modelsData, isLoading: isModelLoading } = useGetModelsQuery(
    { page: 1, limit: 100, categoryId: categoryId, brandId: brandId, type: selectedVehicleType }
  );

  const { data: sparePartDetails } = useGetSparePartDetailsQuery(id, { skip: !isEditMode, refetchOnMountOrArgChange: true });
  const { data: showroomsData } = useGetShowroomsQuery({ page: 1, limit: 100 });
  const showrooms = showroomsData?.data?.data || [];

  useEffect(() => {
    if (isEditMode && sparePartDetails?.data) {
      const data = sparePartDetails.data;
      setName(data.name || "");
      setCategoryId(data.categoryId?._id || data.categoryId || "");
      setSubCategoryId(data.subCategoryId?._id || data.subCategoryId || "");
      setBrandId(data.brand?._id || data.brand || "");
      setModelId(data.model?._id || data.model || "");
      setConditionId(data.condition || "");
      setOemNumber(data.oemNumber || "");
      setActualPrice(data.actualPrice?.toString() || "");
      setDiscountPrice(data.discountPrice?.toString() || "");
      setShortDesc(data.shortDescription || "");
      setIsFeatured(data.isFeatured || false);
      const vType = data.type;
      setSelectedVehicleType(Array.isArray(vType) ? vType[0] : (vType || ""));
      setImages(data.images || []);
      if (data.manufacturingYear) {
        setSelectedYear(dayjs().year(data.manufacturingYear));
      }
      setDealer(data.dealer?._id || data.dealer || "");
    }
  }, [isEditMode, sparePartDetails]);

  const brands = brandsData?.data?.data || [];
  const categoriesList = categoriesData?.data?.data || [];
  const subCategoriesList = categoryDetails?.data?.subCategories || [];
  // const conditionsList = conditionsData?.data?.data || [];
  const models = modelsData?.data?.data || [];

  const [errors, setErrors] = useState<any>({});

  const validate = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (images.length === 0) tempErrors.images = "At least one image is required";
    if (!name.trim()) tempErrors.name = "Name is required";
    if (!dealer) tempErrors.dealer = "Dealer is required";
    if (!categoryId) tempErrors.categoryId = "Category is required";
    if (!subCategoryId) tempErrors.subCategoryId = "Sub Category is required";
    if (!oemNumber.trim()) {
      tempErrors.oemNumber = "OEM number is required.";
    } else if (oemNumber.length > 50) {
      tempErrors.oemNumber = "OEM number cannot exceed 50 characters.";
    } else if (!/^[a-zA-Z0-9-]+$/.test(oemNumber)) {
      tempErrors.oemNumber = "Please enter a valid OEM number. Only letters, numbers, and hyphens are allowed.";
    }
    if (!conditionId) tempErrors.conditionId = "Condition is required";
    if (!actualPrice) {
      tempErrors.actualPrice = "Actual Price is required";
    } else if (isNaN(Number(actualPrice))) {
      tempErrors.actualPrice = "Please enter a valid price";
    } else if (Number(actualPrice) < 1) {
      tempErrors.actualPrice = "Actual Price must be at least 1.";
    }

    if (!discountPrice) {
      tempErrors.discountPrice = "Discounted Price is required";
    } else if (isNaN(Number(discountPrice))) {
      tempErrors.discountPrice = "Please enter a valid price";
    } else if (Number(discountPrice) < 1) {
      tempErrors.discountPrice = "Discounted Price must be at least 1.";
    } else if (Number(discountPrice) >= Number(actualPrice)) {
      tempErrors.discountPrice = "Discounted Price cannot be greater than or equal to Actual Price.";
    }
    if (!shortDesc.trim()) tempErrors.shortDesc = "Description is required";
    if (!brandId) tempErrors.brandId = "Brand is required";
    if (!modelId) tempErrors.modelId = "Model is required";
    if (!selectedVehicleType) tempErrors.vehicleType = "Vehicle Type is required";
    if (!selectedYear) tempErrors.selectedYear = "Year is required";

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      isValid = false;
      toast.error("Please fill all required fields");
    } else {
      setErrors({});
    }
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload = {
        categoryId,
        subCategoryId: subCategoryId || undefined,
        name,
        images,
        oemNumber,
        condition: Number(conditionId),
        actualPrice: Number(actualPrice),
        discountPrice: Number(discountPrice),
        fullDescription: shortDesc,
        shortDescription: shortDesc,
        brand: brandId,
        model: modelId,
        type: Number(selectedVehicleType),
        dealer: dealer,
        manufacturingYear: selectedYear ? dayjs(selectedYear).year() : undefined,
        isFeatured
      };

      if (isEditMode) {
        await updateSparePart({ id, ...payload }).unwrap();
        toast.success("Spare part updated successfully");
      } else {
        await addSparePart(payload).unwrap();
        toast.success("Spare part added successfully");
      }
      navigate("/manage-spare-parts");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save spare part");
    }
  };

  const handleImageUpload = async (file: File) => {
    if (images.length >= 20) {
      toast.error("You can upload a maximum of 20 images");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image size should not exceed 10 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await uploadMedia(formData).unwrap();
      const imageUrl = res?.data?.url || res?.url || res?.data;
      if (imageUrl) {
        setImages([...images, imageUrl]);
        toast.success("Image uploaded");
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Upload failed");
    }
  };


  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title={`${isEditMode ? "تعديل" : "إضافة"} قطعة غيار / ${isEditMode ? "Edit" : "Add"} Spare Part`}
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "قطع الغيار المدرجة / Listed Spare Parts", onClick: () => navigate("/manage-spare-parts") },
          { label: `${isEditMode ? "تعديل" : "إضافة"} قطعة غيار / ${isEditMode ? "Edit" : "Add"} Spare Part` },
        ]}
      />

      <div className="form">

        {/* ================= Spare Part Detail ================= */}
        <div className="cards">
          <div className="table_header mb_20">
            <h2>تفاصيل قطعة الغيار / Spare Part Detail</h2>
          </div>

          <div className="gap_p">

            {/* Image Upload */}
            <div className="control_group w_100">
              <label>تحميل الصور / Upload Images <span className="c_danger">*</span> <small>- {images.length} (JPG, max. 10 MB per Image)</small></label>
              <div className="gap_p">
                <div className="gap_p">
                  {/* Upload Button — always first/leftmost, hidden at 20 image limit */}
                  {images.length < 20 && (
                    <div className="w_25">
                      <label className="upload_image no_radius v2" style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
                        <Input
                          sx={{ display: "none" }}
                          type="file"
                          inputProps={{ accept: "image/*" }}
                          disabled={isUploading}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            const files = event.target.files;
                            if (files && files[0]) {
                              handleImageUpload(files[0]);
                              event.target.value = ""; // Reset input
                            }
                          }}
                        />
                        <span className="upload_image_holder">
                          {isUploading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                              <CircularProgress size={24} />
                            </div>
                          ) : (
                            <figure className="add_img">
                              <img
                                src="/static/images/placeholder.png"
                                alt=""
                              />
                              <span className="upload_img_icon">
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt=""
                                />
                              </span>
                            </figure>
                          )}
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Image Previews — rendered after the upload button */}
                  {images.map((img, index) => (
                    <div className="w_25" key={index}>
                      <div className="upload_image no_radius v2">
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia component="img" image={img} />
                          </figure>
                          <CloseIcon
                            className="remove_img"
                            onClick={() => {
                              const newImages = [...images];
                              newImages.splice(index, 1);
                              setImages(newImages);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {errors.images && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.images}</p>}
            </div>

            {/* Spare Part Name */}
            <div className="control_group w_100">
              <label>اسم قطعة الغيار / Spare Part Name</label>
              <TextField
                placeholder="أدخل اسم قطعة الغيار / Enter Spare Part Name"
                hiddenLabel
                fullWidth
                value={name}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.startsWith(" ")) val = val.trimStart();
                  if (val.length <= 40) {
                    setName(val);
                    if (errors.name) setErrors({ ...errors, name: "" });
                  }
                }}
                error={!!errors.name}
                helperText={errors.name}
              />
            </div>

            <div className="control_group w_33">
              <label>العلامة التجارية / Brand</label>
              <Select
                fullWidth
                displayEmpty
                value={brandId}
                onChange={(e) => {
                  setBrandId(e.target.value);
                  setModelId(""); // Reset model on brand change
                  if (errors.brandId) setErrors({ ...errors, brandId: "" });
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.brandId}
              >
                <MenuItem value="" disabled>حدد العلامة التجارية / Select Brand</MenuItem>
                {brands.map((brand: any) => (
                  <MenuItem key={brand._id} value={brand._id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.brandId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.brandId}</p>}
            </div>

            <div className="control_group w_33">
              <label>الموديل / Model</label>
              <Select
                fullWidth
                displayEmpty
                value={modelId}
                onChange={(e) => {
                  setModelId(e.target.value);
                  if (errors.modelId) setErrors({ ...errors, modelId: "" });
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.modelId}
              >
                <MenuItem value="" disabled>حدد الموديل / Select Model</MenuItem>
                {isModelLoading ? (
                  <MenuItem value="loading" disabled>Loading...</MenuItem>
                ) : models.length > 0 ? (
                  models.map((model: any) => (
                    <MenuItem key={model._id} value={model._id}>
                      {model.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="not_found" disabled>لا توجد موديلات / No Models Found</MenuItem>
                )}
              </Select>
              {errors.modelId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.modelId}</p>}
            </div>

            <div className="control_group w_33">
              <label>السنة / Year</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={['year']}
                  value={selectedYear}
                  onChange={(newValue) => {
                    setSelectedYear(newValue);
                    if (errors.selectedYear) setErrors({ ...errors, selectedYear: "" });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "حدد السنة / Select Year",
                      error: !!errors.selectedYear,
                      helperText: errors.selectedYear
                    }
                  }}
                  disableFuture
                />
              </LocalizationProvider>
            </div>

            <div className="control_group w_33">
              <label htmlFor="VehicleType">نوع المركبة / Vehicle Type</label>
              <Select
                fullWidth
                hiddenLabel
                displayEmpty
                disabled={!!defaultType}
                value={selectedVehicleType}
                onChange={(event) => {
                  setSelectedVehicleType(event.target.value as number);
                  if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
                }}
                renderValue={(selected) => {
                  if (!selected) return "حدد نوع المركبة / Select Vehicle Type";
                  return getVehicleTypeLabel(selected);
                }}
                error={!!errors.vehicleType}
              >
                <MenuItem value="" disabled>حدد نوع المركبة / Select Vehicle Type</MenuItem>
                {Object.keys(VEHICLE_TYPE)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                      {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                    </MenuItem>
                  ))}
              </Select>
              {errors.vehicleType && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.vehicleType}</p>}
            </div>

            {/* Dealer */}
            <div className="control_group w_33">
              <label>المعرض / Showroom <span className="c_danger">*</span></label>
              <Select
                fullWidth
                hiddenLabel
                value={dealer}
                onChange={(e) => {
                  setDealer(e.target.value);
                  if (errors.dealer) setErrors({ ...errors, dealer: "" });
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.dealer}
              >
                <MenuItem value="" disabled>حدد المعرض / Select Showroom</MenuItem>
                {showrooms.map((d: any) => (
                  <MenuItem key={d._id} value={d._id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.dealer && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.dealer}</p>}
            </div>

            {/* Category */}
            <div className="control_group w_33">
              <label>الفئة / Category</label>
              <Select
                fullWidth
                displayEmpty
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setSubCategoryId(""); // Reset sub-category on category change
                  if (errors.categoryId) setErrors({ ...errors, categoryId: "" });
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.categoryId}
              >
                <MenuItem value="" disabled>حدد الفئة / Select Category</MenuItem>
                {categoriesList.map((cat: any) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.categoryId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.categoryId}</p>}
            </div>

            {/* SubCategory */}
            <div className="control_group w_33">
              <label>الفئة الفرعية / Sub Category</label>
              <Select
                fullWidth
                displayEmpty
                value={subCategoryId}
                onChange={(e) => {
                  setSubCategoryId(e.target.value);
                  if (errors.subCategoryId) setErrors({ ...errors, subCategoryId: "" });
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.subCategoryId}
              >
                <MenuItem value="" disabled>{categoryId ? "حدد الفئة الفرعية / Select Sub Category" : "حدد الفئة أولاً / Select Category First"}</MenuItem>
                {isSubCatLoading ? (
                  <MenuItem value="" disabled>Loading...</MenuItem>
                ) : subCategoriesList.length > 0 ? (
                  subCategoriesList.map((sub: any) => (
                    <MenuItem key={sub._id} value={sub._id}>
                      {sub.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>لا توجد فئات فرعية / No Sub Categories Found</MenuItem>
                )}
              </Select>
              {errors.subCategoryId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.subCategoryId}</p>}
            </div>

            {/* OEM */}
            <div className="control_group w_33">
              <label>رقم القطعة الأصلية (OEM) / OEM Number</label>
              <TextField
                placeholder="أدخل رقم القطعة الأصلية / Enter OEM Number"
                hiddenLabel
                fullWidth
                value={oemNumber}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.startsWith(" ")) val = val.trimStart();
                  if (val.length <= 50) {
                    setOemNumber(val);
                    if (errors.oemNumber) setErrors({ ...errors, oemNumber: "" });
                  }
                }}
                inputProps={{ maxLength: 50 }}
                error={!!errors.oemNumber}
                helperText={errors.oemNumber}
              />
            </div>

            {/* Condition */}
            <div className="control_group w_33">
              <label>الحالة / Condition</label>
              <Select
                fullWidth
                displayEmpty
                value={conditionId}
                onChange={(e) => {
                  setConditionId(e.target.value);
                  if (errors.conditionId) setErrors({ ...errors, conditionId: "" });
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                }}
                error={!!errors.conditionId}
              >
                <MenuItem value="" disabled>حدد الحالة / Select Condition</MenuItem>
                {Object.keys(VEHICLE_CONDITION)
                  .filter(k => isNaN(Number(k)))
                  .map((key) => (
                    <MenuItem key={key} value={(VEHICLE_CONDITION as any)[key]}>
                      {key.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
              </Select>
              {errors.conditionId && <p className="error_text" style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "3px 14px 0" }}>{errors.conditionId}</p>}
            </div>

            {/* Prices */}
            <div className="control_group w_33">
              <div className="gap_p">
                <div className="control_group w_50">
                  <label>السعر الفعلي / Actual Price</label>
                  <TextField
                    placeholder="السعر الفعلي / Actual Price"
                    hiddenLabel
                    fullWidth
                    value={actualPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setActualPrice(val);
                        if (errors.actualPrice) setErrors({ ...errors, actualPrice: "" });
                      }
                    }}
                    error={!!errors.actualPrice}
                    helperText={errors.actualPrice}
                  />
                </div>

                <div className="control_group w_50">
                  <label>السعر المخفض / Discounted Price</label>
                  <TextField
                    placeholder="السعر المخفض / Discounted Price"
                    hiddenLabel
                    fullWidth
                    value={discountPrice}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                        setDiscountPrice(val);
                        if (errors.discountPrice) setErrors({ ...errors, discountPrice: "" });
                      }
                    }}
                    error={!!errors.discountPrice}
                    helperText={errors.discountPrice}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="control_group w_100">
              <label>الوصف / Description</label>
              <TextField
                multiline
                rows={4}
                placeholder="أدخل الوصف / Enter description"
                hiddenLabel
                fullWidth
                value={shortDesc}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.startsWith(" ")) val = val.trimStart();
                  if (val.length <= 800) {
                    setShortDesc(val);
                    if (errors.shortDesc) setErrors({ ...errors, shortDesc: "" });
                  }
                }}
                inputProps={{ maxLength: 800 }}
                error={!!errors.shortDesc}
                helperText={errors.shortDesc}
              />
            </div>

            {/* Featured */}
            <div className="control_group flex w_100">
              <label>هل هذه قطعة غيار مميزة؟ / Is this Featured Spare Part?</label>
              <SwitchToggle checked={isFeatured} onChange={() => setIsFeatured(!isFeatured)} />
            </div>
          </div>
        </div>



        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button className="btn btn_primary" onClick={handleSubmit} disabled={isAdding}>
            {isAdding ? "جارٍ الحفظ... / Saving..." : "حفظ / Save"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddEditSparePart;